// src/components/BI/DateRangeSelector.jsx
import React, { useState } from 'react';
import { Row, Col, Button, ButtonGroup, Label } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DateRangeSelector = ({ dateRange, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);

  const presets = [
    { label: 'Last 7 Days', value: 'last_7_days', days: 7 },
    { label: 'Last 30 Days', value: 'last_30_days', days: 30 },
    { label: 'Last 3 Months', value: 'last_3_months', months: 3 },
    { label: 'Last 6 Months', value: 'last_6_months', months: 6 },
    { label: 'Last Year', value: 'last_year', years: 1 },
    { label: 'Year to Date', value: 'ytd' },
    { label: 'Custom', value: 'custom' },
  ];

  const handlePresetClick = (preset) => {
    const endDate = new Date();
    let startDate = new Date();

    if (preset.value === 'ytd') {
      startDate = new Date(endDate.getFullYear(), 0, 1);
    } else if (preset.days) {
      startDate.setDate(startDate.getDate() - preset.days);
    } else if (preset.months) {
      startDate.setMonth(startDate.getMonth() - preset.months);
    } else if (preset.years) {
      startDate.setFullYear(startDate.getFullYear() - preset.years);
    } else if (preset.value === 'custom') {
      setShowCustom(true);
      return;
    }

    setShowCustom(false);
    
    // Default granularity based on date range
    let granularity = 'month';
    const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff <= 31) {
      granularity = 'day'; // Use daily for ranges <= 1 month
    }

    onChange({ startDate, endDate, preset: preset.value, granularity });
  };

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    if (start && end) {
      // Determine granularity based on range
      const daysDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      const granularity = daysDiff <= 31 ? 'day' : 'month';
      
      onChange({ 
        startDate: start, 
        endDate: end, 
        preset: 'custom',
        granularity 
      });
    }
  };

  const handleGranularityChange = (newGranularity) => {
    onChange({ 
      ...dateRange, 
      granularity: newGranularity 
    });
  };

  return (
    <div className="date-range-selector">
      <Row className="mb-3">
        <Col>
          <Label className="font-weight-bold">Date Range Presets:</Label>
          <div className="d-flex flex-wrap gap-2">
            {presets.map((preset) => (
              <Button
                key={preset.value}
                color={dateRange.preset === preset.value ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </Col>
      </Row>

      {showCustom && (
        <Row className="mb-3">
          <Col md={6}>
            <Label>Select Date Range:</Label>
            <DatePicker
              selectsRange
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleCustomDateChange}
              className="form-control"
              dateFormat="yyyy-MM-dd"
              maxDate={new Date()}
            />
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col>
          <Label className="font-weight-bold">View By:</Label>
          <ButtonGroup size="sm">
            <Button
              color={dateRange.granularity === 'day' ? 'success' : 'outline-success'}
              onClick={() => handleGranularityChange('day')}
            >
              📅 Daily
            </Button>
            <Button
              color={dateRange.granularity === 'month' ? 'success' : 'outline-success'}
              onClick={() => handleGranularityChange('month')}
            >
              📊 Monthly
            </Button>
          </ButtonGroup>
          <small className="text-muted d-block mt-1">
            {dateRange.granularity === 'day' 
              ? 'Data grouped by day (best for ranges ≤ 1 month)'
              : 'Data grouped by month (best for longer ranges)'}
          </small>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="selected-range bg-light p-2 rounded">
            <strong>Selected Range:</strong>{' '}
            {dateRange.startDate.toLocaleDateString()} to {dateRange.endDate.toLocaleDateString()}
            {' '}
            <span className="badge badge-info ms-2">
              {dateRange.granularity === 'day' ? 'Daily View' : 'Monthly View'}
            </span>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DateRangeSelector;